import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Flex, Grid, Typography } from '@percent/lemonade'
import { EligibilityQuestion, QuestionnaireQuestion } from './grantAdvisoryBooking.utils'
import styles from './GrantAdvisoryBookingModal.module.scss'

type Option = {
  value: string
  labelKey: string
  disqualifies?: boolean
  // The user needs customer support, not AgentPro, so it routes to the support form.
  routesToSupport?: boolean
}

// "No" to an eligibility question means the service cannot work with the org, so it routes to Sorry.
// Rendered in this order, so No sits on the left and Yes on the right.
const YES_NO_OPTIONS: Option[] = [
  { value: 'no', labelKey: 'grantAdvisoryQuestionnaire.options.no', disqualifies: true },
  { value: 'yes', labelKey: 'grantAdvisoryQuestionnaire.options.yes' }
]

// Here "Yes" is the exit, not "No". The order stays No-left, Yes-right so the buttons
// don't swap places between questions.
const DISCOUNT_SUPPORT_OPTIONS: Option[] = [
  { value: 'no', labelKey: 'grantAdvisoryQuestionnaire.options.no' },
  { value: 'yes', labelKey: 'grantAdvisoryQuestionnaire.options.yes', routesToSupport: true }
]

const QUESTIONS: { question: EligibilityQuestion; questionKey: string; options: Option[] }[] = [
  {
    question: 'discountSupport',
    questionKey: 'grantAdvisoryQuestionnaire.discountSupport.question',
    options: DISCOUNT_SUPPORT_OPTIONS
  },
  {
    question: 'registeredCharity',
    questionKey: 'grantAdvisoryQuestionnaire.registeredCharity.question',
    options: YES_NO_OPTIONS
  },
  {
    question: 'overOneYear',
    questionKey: 'grantAdvisoryQuestionnaire.overOneYear.question',
    options: YES_NO_OPTIONS
  }
]

export type GrantEligibilityQuestionnaireProps = {
  onStart: () => void
  onAnswer: (question: QuestionnaireQuestion, answer: string, disqualifying: boolean) => void
  onDisqualify: (reason: QuestionnaireQuestion) => void
  onSupport: (reason: QuestionnaireQuestion) => void
  onQualified: () => void
}

// The sidebar promo's three-question Yes/No check. Smart Apply keeps the experiment's
// GrantQuestionnaire.
export const GrantEligibilityQuestionnaire = ({
  onStart,
  onAnswer,
  onDisqualify,
  onSupport,
  onQualified
}: GrantEligibilityQuestionnaireProps) => {
  const { t } = useTranslation()

  const translate = t as (key: string) => string
  const [step, setStep] = useState(1)

  // Mount-only: onStart tracks "questionnaire started", which must fire once, not on every
  // parent re-render that passes a new callback.
  useEffect(() => {
    onStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const current = QUESTIONS[step - 1]

  // Each answer button submits its answer and advances, so there is no separate Next step.
  const handleAnswer = (option: Option) => {
    onAnswer(current.question, option.value, Boolean(option.disqualifies))

    if (option.routesToSupport) {
      onSupport(current.question)
      return
    }

    if (option.disqualifies) {
      onDisqualify(current.question)
      return
    }

    if (step >= QUESTIONS.length) {
      onQualified()
      return
    }

    setStep(step + 1)
  }

  const handleBack = () => setStep(prev => Math.max(1, prev - 1))

  return (
    <div className={styles.eligibilityStep}>
      <div
        key={step}
        className={`${styles.eligibilityStepBody} ${styles.stepEnter}`}
        data-testid={`grant-questionnaire-${current.question}`}
      >
        <Flex direction="column" gap={16}>
          <Typography variant="small" $color="gray500" className={styles.centeredText}>
            {t('grantAdvisoryQuestionnaire.progress', { current: step, total: QUESTIONS.length })}
          </Typography>
          <Typography variant="h5" className={styles.centeredText}>
            {translate(current.questionKey)}
          </Typography>
          <Grid gutter="var(--spacing-s)">
            {current.options.map(option => (
              <Grid.Col span="auto" key={option.value}>
                <Button
                  variant="secondary"
                  stretch
                  onClick={() => handleAnswer(option)}
                  data-testid={`grant-questionnaire-answer-${option.value}`}
                >
                  {translate(option.labelKey)}
                </Button>
              </Grid.Col>
            ))}
          </Grid>
        </Flex>
      </div>

      <Flex gap={12} className={styles.eligibilityStepFooter}>
        <Button
          variant="secondary"
          onClick={handleBack}
          disabled={step === 1}
          data-testid="grant-questionnaire-back-button"
        >
          {t('grantAdvisoryQuestionnaire.back')}
        </Button>
      </Flex>
    </div>
  )
}
